import { msg } from "@lingui/core/macro";
import { type Component, type JSX, Show } from "solid-js";
import { useI18n } from "@/providers/i18n-provider";
import { EmptyState } from "./empty-state";
import { Button } from "./button";

type NotFoundVariant = "not-found" | "no-access" | "offline";

interface NotFoundStateProps {
  variant?: NotFoundVariant;
  /** Custom title and description (override the default copy). */
  title?: string;
  description?: string;
  /** Primary action (e.g. return home / to parent screen). */
  primaryAction?: { label: string; onClick: () => void };
  /** Optional secondary action (e.g. retry). */
  secondaryAction?: { label: string; onClick: () => void };
  class?: string;
  "data-testid"?: string;
}

/**
 * Thematic culinary illustration (theme-aware) by variant. Reuses the
 * `EmptyState` set to maintain a consistent visual identity rather than
 * generic icons.
 */
const ILLUSTRATIONS: Record<NotFoundVariant, "plate" | "menu" | "tables"> = {
  "not-found": "plate",
  "no-access": "menu",
  offline: "tables",
};

/**
 * "Not found / no access / offline" state for routes and detail views
 * whose entity could not be resolved (see `createEntityResolution`).
 * Reuses the premium `EmptyState` (brand-aware illustration, fade-in under the
 * global reduced-motion guard) and provides default localized copy +
 * clear actions, instead of leaving the screen loading indefinitely or
 * showing a deceptive empty view.
 */
export const NotFoundState: Component<NotFoundStateProps> = (props) => {
  const { t } = useI18n();
  const variant = (): NotFoundVariant => props.variant ?? "not-found";

  const defaultTitle = (): string => {
    if (variant() === "no-access") {
      return t(msg`You don't have access`);
    }
    if (variant() === "offline") {
      return t(msg`Can't load this yet`);
    }
    return t(msg`Not found`);
  };

  const defaultDescription = (): string => {
    if (variant() === "no-access") {
      return t(msg`This item doesn't exist or you don't have permission to view it.`);
    }
    if (variant() === "offline") {
      return t(
        msg`You're offline and this hasn't synced to this device yet. Reconnect and try again.`,
      );
    }
    return t(msg`This item may have been deleted, or the link is no longer valid.`);
  };

  const action = (): JSX.Element => (
    <div class="flex flex-col items-center gap-2 sm:flex-row">
      <Show when={props.primaryAction}>
        {(primary) => (
          <Button
            onClick={() => {
              primary().onClick();
            }}
          >
            {primary().label}
          </Button>
        )}
      </Show>
      <Show when={props.secondaryAction}>
        {(secondary) => (
          <Button
            variant="outline"
            onClick={() => {
              secondary().onClick();
            }}
          >
            {secondary().label}
          </Button>
        )}
      </Show>
    </div>
  );

  // The illustration forwards the primary action (or the secondary if no
  // primary) so tapping the icon triggers the CTA, just like the button.
  const iconAction = (): { label: string; onClick: () => void } | undefined =>
    props.primaryAction ?? props.secondaryAction;

  return (
    <EmptyState
      data-testid={props["data-testid"]}
      illustration={ILLUSTRATIONS[variant()]}
      title={props.title ?? defaultTitle()}
      description={props.description ?? defaultDescription()}
      action={(props.primaryAction ?? props.secondaryAction) ? action() : undefined}
      onIconActivate={iconAction() ? () => iconAction()?.onClick() : undefined}
      iconActionLabel={iconAction()?.label}
      class={props.class}
    />
  );
};
